import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  X, Brain, ShieldCheckered, ShieldCheck, ShieldWarning, FileLock,
  ListMagnifyingGlass, FlowArrow, ArrowsClockwise, Wrench, Database,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * ComplianceInvestigationModal
 *   Auditor drill-down on a SOC 2 control. Pulls live evidence and the Strategic Partner
 *   4.7 auditor summary from `/api/compliance/investigate/{slug}`.
 *   Three sections, top-to-bottom:
 *     1. Raw Evidence (per-assertion SQL + offending row IDs · or scan logs)
 *     2. Strategic Partner Summary (Strategic Partner (Lead))
 *     3. Remediation Steps + Evidence Pointer
 * ──────────────────────────────────────────────────────────────────────── */

const VERDICT_TONE = {
  CLEAN:     { fg: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", icon: ShieldCheck },
  ATTENTION: { fg: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/40", icon: ShieldWarning },
  BLOCKING:  { fg: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-500/40", icon: ShieldWarning },
};

export default function ComplianceInvestigationModal({ control, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!control) return;
    setData(null); setError(null);
    api.complianceInvestigate(control.slug)
      .then(setData)
      .catch((e) => setError(e?.message || "Investigation fetch failed"));
  }, [control]);

  if (!control) return null;

  return (
    <div
      data-testid="compliance-investigation-modal"
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-3xl h-full bg-slate-900 border-l border-slate-700 flex flex-col"
      >
        {/* Header */}
        <header className="px-5 py-3 border-b border-slate-800 bg-slate-950 flex items-start justify-between gap-3">
          <div>
            <div className="tracking-mono-label text-[10px] text-emerald-300 mb-1 flex items-center gap-2">
              <ShieldCheckered weight="duotone" size={12} /> Investigation Trace
            </div>
            <h3 className="font-display text-base font-semibold text-slate-50">
              {control.name}
            </h3>
            <p className="text-[11px] text-slate-500 font-mono mt-0.5">
              SOC 2 · {control.control} · status {control.status} · {control.findings} finding
              {control.findings === 1 ? "" : "s"} · slug <span className="text-slate-400">{control.slug}</span>
            </p>
          </div>
          <button
            onClick={onClose} data-testid="investigation-close"
            className="p-1 rounded-sm text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-auto thin-scroll">
          {!data && !error && <Loading />}
          {error && <ErrorBanner error={error} />}
          {data && <Body data={data} />}
        </div>
      </aside>
    </div>
  );
}

/* ─── Body ─────────────────────────────────────────────────────────────── */
function Body({ data }) {
  const verdict = data.auditor_summary?.verdict || "ATTENTION";
  const tone = VERDICT_TONE[verdict] || VERDICT_TONE.ATTENTION;
  const Icon = tone.icon;

  return (
    <div className="p-5 space-y-5">
      {/* Verdict band */}
      <div
        data-testid="investigation-verdict"
        className={`rounded-sm border p-3 flex items-center gap-3 ${tone.border} ${tone.bg}`}
      >
        <Icon weight="fill" className={tone.fg} size={20} />
        <div className="flex-1 min-w-0">
          <div className={`tracking-mono-label text-[10px] ${tone.fg}`}>
            Strategic Partner verdict · {verdict}
          </div>
          <div className="font-mono text-[10px] text-slate-400 mt-0.5">
            scan targets · {(data.evidence?.scan_targets || []).slice(0, 2).join(" · ")}
            {(data.evidence?.scan_targets || []).length > 2 ? ` · +${data.evidence.scan_targets.length - 2}` : ""}
          </div>
        </div>
      </div>

      {/* SECTION 1 · Raw Evidence */}
      <Section icon={ListMagnifyingGlass} title="Raw Evidence" testid="section-evidence">
        <RawEvidence evidence={data.evidence} />
      </Section>

      {/* SECTION 2 · Strategic Partner Summary (Strategic Partner) */}
      <Section icon={Brain} title="Strategic Partner Summary" testid="section-summary">
        <p className="text-sm text-slate-100 leading-relaxed" data-testid="auditor-summary">
          {data.auditor_summary?.auditor_summary || "—"}
        </p>
        {data.auditor_summary?.residual_risk && (
          <div className="mt-3 border-l-2 border-amber-500/40 pl-3 py-1">
            <div className="tracking-mono-label text-[10px] text-amber-300 mb-0.5">Residual risk</div>
            <p className="text-[12px] text-slate-200 leading-snug">
              {data.auditor_summary.residual_risk}
            </p>
          </div>
        )}
        {data.auditor_summary?.opus_trace && (
          <details className="mt-3 group" data-testid="opus-trace-details">
            <summary className="cursor-pointer text-[10px] tracking-mono-label text-fuchsia-300 hover:text-fuchsia-200">
              <FlowArrow weight="bold" size={9} className="inline mr-1" /> strategic partner reasoning trace
            </summary>
            <p className="mt-2 text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap font-mono">
              {data.auditor_summary.opus_trace}
            </p>
          </details>
        )}
      </Section>

      {/* SECTION 3 · Remediation Steps */}
      <Section icon={Wrench} title="Remediation Steps" testid="section-remediation">
        {data.auditor_summary?.remediation_step ? (
          <div className="border-l-2 border-emerald-500/40 pl-3 py-1" data-testid="remediation-step">
            <p className="text-sm text-slate-100 leading-snug">{data.auditor_summary.remediation_step}</p>
          </div>
        ) : (
          <p className="text-[11px] font-mono text-slate-500">No remediation required — control is clean.</p>
        )}
        {data.auditor_summary?.evidence_pointer && (
          <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border border-slate-700 bg-slate-950 text-[11px] font-mono text-slate-300">
            <Database size={11} className="text-slate-400" />
            evidence · {data.auditor_summary.evidence_pointer}
          </div>
        )}
      </Section>
    </div>
  );
}

/* ─── Section atom ─────────────────────────────────────────────────────── */
function Section({ icon, title, children, testid }) {
  const Icon = icon;
  return (
    <section data-testid={testid}>
      <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-2">
        <Icon weight="duotone" size={12} /> {title}
      </div>
      <div className="bg-slate-950 border border-slate-800 rounded-sm p-4">
        {children}
      </div>
    </section>
  );
}

/* ─── Raw Evidence renderer (per-investigator-kind) ────────────────────── */
function RawEvidence({ evidence }) {
  if (!evidence) return <p className="text-[11px] font-mono text-slate-500">—</p>;
  const findings = evidence.findings || [];

  // SQL-assertion shape: every finding carries `id`, `sql`, `expected`, `actual_live`, `status`.
  const isSqlAssertions = findings.length > 0 && findings[0]?.sql && findings[0]?.expected !== undefined;
  if (isSqlAssertions) {
    return (
      <ul className="space-y-2" data-testid="evidence-sql-assertions">
        {findings.map((f, i) => {
          const pass = f.status === "PASS";
          return (
            <li key={f.id || i} className={`p-2.5 rounded-sm border ${
              pass ? "border-emerald-500/30 bg-emerald-500/5"
                   : "border-rose-500/40 bg-rose-500/5"}`}
                data-testid={`evidence-assertion-${f.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className={`font-mono text-[10px] tracking-mono-label
                  ${pass ? "text-emerald-300" : "text-rose-300"}`}>
                  {f.id} · {f.status}
                </span>
                <span className="font-mono text-[10px] text-slate-500">
                  expected {String(f.expected)} · actual {String(f.actual_live)}
                </span>
              </div>
              <div className="text-[11px] text-slate-100 mt-0.5 leading-snug">{f.name}</div>
              <pre className="mt-2 text-[10px] font-mono text-slate-300 whitespace-pre-wrap break-all bg-slate-950 border border-slate-800 rounded-sm p-2">
                {f.sql}
              </pre>
              {(f.offending_rows || []).length > 0 && (
                <div className="mt-2">
                  <div className="tracking-mono-label text-[9px] text-rose-300 mb-1">offending rows ({f.offending_rows.length})</div>
                  <pre className="text-[10px] font-mono text-slate-200 bg-slate-950 border border-slate-800 rounded-sm p-2 max-h-[160px] overflow-auto">
                    {JSON.stringify(f.offending_rows, null, 2)}
                  </pre>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  // Gitleaks-history shape
  const isLeakDays = findings.length > 0 && findings[0]?.commits_scanned !== undefined;
  if (isLeakDays) {
    return (
      <ul className="space-y-1.5 text-[11px] font-mono text-slate-200" data-testid="evidence-gitleaks">
        {findings.map((d, i) => (
          <li key={i}>
            <FileLock weight="bold" size={10} className="inline mr-1 text-rose-300" />
            {d.date} · {d.commits_scanned} commits · <span className="text-rose-300">{d.leaks_found} leaks</span>
            {d.resolved && <span className="text-emerald-300"> · resolved</span>}
          </li>
        ))}
      </ul>
    );
  }

  // Empty findings — show the auxiliary panel + scan targets
  return (
    <div className="space-y-3" data-testid="evidence-clean">
      <div className="flex items-center gap-2 text-emerald-300 text-[12px]">
        <ShieldCheck weight="fill" size={14} /> no findings on this scan
      </div>
      {(evidence.scan_targets || []).length > 0 && (
        <div>
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-1">scan targets</div>
          <ul className="text-[11px] font-mono text-slate-200 space-y-0.5">
            {evidence.scan_targets.map((t, i) => <li key={i}>· {t}</li>)}
          </ul>
        </div>
      )}
      {evidence.auxiliary && Object.keys(evidence.auxiliary).length > 0 && (
        <div>
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-1">scan auxiliary</div>
          <pre className="text-[10px] font-mono text-slate-300 bg-slate-900 border border-slate-800 rounded-sm p-2 max-h-[260px] overflow-auto whitespace-pre-wrap">
            {JSON.stringify(evidence.auxiliary, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ─── Loading + Error states ────────────────────────────────────────────── */
function Loading() {
  return (
    <div className="p-5 space-y-3" data-testid="investigation-loading">
      <div className="flex items-center gap-2 text-emerald-300 text-[12px]">
        <ArrowsClockwise size={14} className="animate-spin" /> strategic partner generating auditor summary…
      </div>
      <div className="h-24 skeleton rounded-sm" />
      <div className="h-32 skeleton rounded-sm" />
      <div className="h-20 skeleton rounded-sm" />
    </div>
  );
}
function ErrorBanner({ error }) {
  return (
    <div className="p-5" data-testid="investigation-error">
      <div className="text-rose-300 text-xs font-mono">Investigation failed · {error}</div>
    </div>
  );
}
